export interface IStabilizedMaskTuning {
  combineWithPreviousRatio: number
  maskBlurRadius: number
  edgeLow: number
  edgeHigh: number
}

export const defaultStabilizedMaskTuning: IStabilizedMaskTuning = {
  combineWithPreviousRatio: 0.7,
  maskBlurRadius: 8,
  edgeLow: 0.35,
  edgeHigh: 0.75,
}

export interface IStabilizedBackgroundGL {
  renderFrame(frame: VideoFrame): void
  updateMask(maskTexture: WebGLTexture): void
  setBackgroundImage(image: ImageBitmap | null): Promise<void>
  setBlurRadius(radius: number | null): void
  setBackgroundDisabled(disabled: boolean): void
  setMaskInvert(invert: boolean): void
  cleanup(): void
}

const DOWNSAMPLE_FACTOR = 4

const vertexShaderSource = (flipY = true) => `#version 300 es
  in vec2 position;
  out vec2 texCoords;

  void main() {
    texCoords = (position + 1.0) / 2.0;
    texCoords.y = ${flipY ? '1.0 - texCoords.y' : 'texCoords.y'};
    gl_Position = vec4(position, 0, 1.0);
  }
`

const blurFragmentShader = `#version 300 es
  precision mediump float;
  in vec2 texCoords;
  uniform sampler2D u_texture;
  uniform vec2 u_texelSize;
  uniform vec2 u_direction;
  uniform float u_radius;
  out vec4 fragColor;

  void main() {
    float sigma = u_radius;
    float twoSigmaSq = 2.0 * sigma * sigma;
    float totalWeight = 0.0;
    vec3 result = vec3(0.0);
    const int MAX_SAMPLES = 16;
    int radius = int(min(float(MAX_SAMPLES), ceil(u_radius)));

    for (int i = -MAX_SAMPLES; i <= MAX_SAMPLES; ++i) {
      float offset = float(i);
      if (abs(offset) > float(radius)) continue;
      float weight = exp(-(offset * offset) / twoSigmaSq);
      vec2 sampleCoord = texCoords + u_direction * u_texelSize * offset;
      result += texture(u_texture, sampleCoord).rgb * weight;
      totalWeight += weight;
    }

    fragColor = vec4(result / totalWeight, 1.0);
  }
`

const boxBlurFragmentShader = `#version 300 es
  precision mediump float;
  in vec2 texCoords;
  uniform sampler2D u_texture;
  uniform vec2 u_texelSize;
  uniform vec2 u_direction;
  uniform float u_radius;
  out vec4 fragColor;

  void main() {
    vec3 sum = vec3(0.0);
    float count = 0.0;
    const int MAX_RADIUS = 16;
    int radius = int(min(float(MAX_RADIUS), u_radius));

    for (int i = -MAX_RADIUS; i <= MAX_RADIUS; ++i) {
      if (abs(i) > radius) continue;
      vec2 offset = u_direction * u_texelSize * float(i);
      sum += texture(u_texture, texCoords + offset).rgb;
      count += 1.0;
    }

    fragColor = vec4(sum / count, 1.0);
  }
`

const temporalFragmentShader = `#version 300 es
  precision mediump float;
  in vec2 texCoords;
  uniform sampler2D u_currentMask;
  uniform sampler2D u_previousMask;
  uniform float u_combineRatio;
  uniform bool u_invertMask;
  out vec4 fragColor;

  void main() {
    float current = texture(u_currentMask, texCoords).r;
    if (u_invertMask) {
      current = 1.0 - current;
    }
    float previous = texture(u_previousMask, texCoords).r;
    float t = current - 0.5;
    float x = t * t;
    float polynomial = x * (5.68842 + x * (-0.748699 + x * (-57.8051 + x * (291.309 + x * (-624.717)))));
    float uncertainty = 1.0 - min(1.0, polynomial);
    float smoothed = current + (previous - current) * (uncertainty * u_combineRatio);
    fragColor = vec4(smoothed, smoothed, smoothed, 1.0);
  }
`

const compositeFragmentShader = `#version 300 es
  precision mediump float;
  in vec2 texCoords;
  uniform sampler2D background;
  uniform bool disableBackground;
  uniform sampler2D frame;
  uniform sampler2D mask;
  uniform float u_edgeLow;
  uniform float u_edgeHigh;
  out vec4 fragColor;

  void main() {
    vec4 frameTex = texture(frame, texCoords);

    if (disableBackground) {
      fragColor = frameTex;
    } else {
      vec4 bgTex = texture(background, texCoords);
      float personConfidence = texture(mask, texCoords).r;
      float alpha = smoothstep(u_edgeLow, u_edgeHigh, personConfidence);
      fragColor = mix(bgTex, vec4(frameTex.rgb, 1.0), alpha);
    }
  }
`

function createShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)
  if (!shader) {
    throw new Error('Failed to create shader')
  }
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader)
    gl.deleteShader(shader)
    throw new Error(`Shader compile failed: ${info}`)
  }
  return shader
}

function createProgram(gl: WebGL2RenderingContext, vs: WebGLShader, fs: WebGLShader): WebGLProgram {
  const program = gl.createProgram()
  if (!program) {
    throw new Error('Failed to create program')
  }
  gl.attachShader(program, vs)
  gl.attachShader(program, fs)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`Program link failed: ${gl.getProgramInfoLog(program)}`)
  }
  gl.detachShader(program, vs)
  gl.detachShader(program, fs)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  return program
}

function initTexture(gl: WebGL2RenderingContext, texIndex: number): WebGLTexture {
  const texture = gl.createTexture()
  if (!texture) {
    throw new Error('Failed to create texture')
  }
  gl.activeTexture(gl.TEXTURE0 + texIndex)
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  return texture
}

function createFramebuffer(
  gl: WebGL2RenderingContext,
  texture: WebGLTexture,
  width: number,
  height: number,
): WebGLFramebuffer {
  const framebuffer = gl.createFramebuffer()
  if (!framebuffer) {
    throw new Error('Failed to create framebuffer')
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0)
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    throw new Error('Framebuffer not complete')
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  return framebuffer
}

function createVertexBuffer(gl: WebGL2RenderingContext): WebGLBuffer {
  const vertexBuffer = gl.createBuffer()
  if (!vertexBuffer) {
    throw new Error('Failed to create vertex buffer')
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, -1, 1, 1, 1, -1, -1, 1, 1, 1, -1]),
    gl.STATIC_DRAW,
  )
  return vertexBuffer
}

interface IBlurProgram {
  program: WebGLProgram
  uniforms: {
    position: number
    texture: WebGLUniformLocation | null
    texelSize: WebGLUniformLocation | null
    direction: WebGLUniformLocation | null
    radius: WebGLUniformLocation | null
  }
}

function createBlurProgram(gl: WebGL2RenderingContext, fragmentSource: string): IBlurProgram {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource())
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource)
  const program = createProgram(gl, vertexShader, fragmentShader)
  return {
    program,
    uniforms: {
      position: gl.getAttribLocation(program, 'position'),
      texture: gl.getUniformLocation(program, 'u_texture'),
      texelSize: gl.getUniformLocation(program, 'u_texelSize'),
      direction: gl.getUniformLocation(program, 'u_direction'),
      radius: gl.getUniformLocation(program, 'u_radius'),
    },
  }
}

function applyBlur(
  gl: WebGL2RenderingContext,
  sourceTexture: WebGLTexture,
  width: number,
  height: number,
  blurRadius: number,
  blur: IBlurProgram,
  vertexBuffer: WebGLBuffer,
  processFramebuffers: WebGLFramebuffer[],
  processTextures: WebGLTexture[],
): WebGLTexture {
  gl.useProgram(blur.program)
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer)
  gl.vertexAttribPointer(blur.uniforms.position, 2, gl.FLOAT, false, 0, 0)
  gl.enableVertexAttribArray(blur.uniforms.position)
  gl.bindFramebuffer(gl.FRAMEBUFFER, processFramebuffers[0])
  gl.viewport(0, 0, width, height)
  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, sourceTexture)
  gl.uniform1i(blur.uniforms.texture, 0)
  gl.uniform2f(blur.uniforms.texelSize, 1 / width, 1 / height)
  gl.uniform2f(blur.uniforms.direction, 1, 0)
  gl.uniform1f(blur.uniforms.radius, blurRadius)
  gl.drawArrays(gl.TRIANGLES, 0, 6)
  gl.bindFramebuffer(gl.FRAMEBUFFER, processFramebuffers[1])
  gl.viewport(0, 0, width, height)
  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, processTextures[0])
  gl.uniform1i(blur.uniforms.texture, 0)
  gl.uniform2f(blur.uniforms.direction, 0, 1)
  gl.drawArrays(gl.TRIANGLES, 0, 6)
  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  return processTextures[1]
}

interface IDownSampler {
  framebuffer: WebGLFramebuffer
  texture: WebGLTexture
  program: WebGLProgram
  uniforms: {
    texture: WebGLUniformLocation | null
    position: number
  }
}

function createDownSampler(gl: WebGL2RenderingContext, width: number, height: number): IDownSampler {
  const texture = initTexture(gl, 5)
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
  const framebuffer = gl.createFramebuffer()
  if (!framebuffer) {
    throw new Error('Failed to create framebuffer')
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0)
  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource(false))
  const fragmentSource = `#version 300 es
    precision mediump float;
    in vec2 texCoords;
    uniform sampler2D u_texture;
    out vec4 fragColor;
    void main() {
      fragColor = texture(u_texture, texCoords);
    }
  `
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource)
  const program = createProgram(gl, vertexShader, fragmentShader)
  return {
    framebuffer,
    texture,
    program,
    uniforms: {
      texture: gl.getUniformLocation(program, 'u_texture'),
      position: gl.getAttribLocation(program, 'position'),
    },
  }
}

function applyDownsampling(
  gl: WebGL2RenderingContext,
  inputTexture: WebGLTexture,
  downSampler: IDownSampler,
  vertexBuffer: WebGLBuffer,
  width: number,
  height: number,
): WebGLTexture {
  gl.useProgram(downSampler.program)
  gl.bindFramebuffer(gl.FRAMEBUFFER, downSampler.framebuffer)
  gl.viewport(0, 0, width, height)
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer)
  gl.enableVertexAttribArray(downSampler.uniforms.position)
  gl.vertexAttribPointer(downSampler.uniforms.position, 2, gl.FLOAT, false, 0, 0)
  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, inputTexture)
  gl.uniform1i(downSampler.uniforms.texture, 0)
  gl.drawArrays(gl.TRIANGLES, 0, 6)
  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  return downSampler.texture
}

async function resizeImageToCover(
  image: ImageBitmap,
  targetWidth: number,
  targetHeight: number,
): Promise<ImageBitmap> {
  const imgAspect = image.width / image.height
  const targetAspect = targetWidth / targetHeight
  let sx = 0
  let sy = 0
  let sWidth = image.width
  let sHeight = image.height
  if (imgAspect > targetAspect) {
    sWidth = Math.round(image.height * targetAspect)
    sx = Math.round((image.width - sWidth) / 2)
  } else if (imgAspect < targetAspect) {
    sHeight = Math.round(image.width / targetAspect)
    sy = Math.round((image.height - sHeight) / 2)
  }
  return createImageBitmap(image, sx, sy, sWidth, sHeight, {
    resizeWidth: targetWidth,
    resizeHeight: targetHeight,
    resizeQuality: 'medium',
  })
}

export function setupStabilizedBackgroundGL(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  tuning: IStabilizedMaskTuning = defaultStabilizedMaskTuning,
): IStabilizedBackgroundGL | undefined {
  const gl = canvas.getContext('webgl2', {
    antialias: true,
    premultipliedAlpha: true,
  }) as WebGL2RenderingContext | null
  if (!gl) {
    return undefined
  }

  let blurRadius: number | null = null
  let fboWidth = Math.max(1, canvas.width)
  let fboHeight = Math.max(1, canvas.height)

  gl.enable(gl.BLEND)
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

  const compositeVertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource())
  const compositeFragShader = createShader(gl, gl.FRAGMENT_SHADER, compositeFragmentShader)
  const compositeProgram = createProgram(gl, compositeVertexShader, compositeFragShader)
  const compositeUniforms = {
    position: gl.getAttribLocation(compositeProgram, 'position'),
    mask: gl.getUniformLocation(compositeProgram, 'mask'),
    frame: gl.getUniformLocation(compositeProgram, 'frame'),
    background: gl.getUniformLocation(compositeProgram, 'background'),
    disableBackground: gl.getUniformLocation(compositeProgram, 'disableBackground'),
    edgeLow: gl.getUniformLocation(compositeProgram, 'u_edgeLow'),
    edgeHigh: gl.getUniformLocation(compositeProgram, 'u_edgeHigh'),
  }

  const temporalVertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource(false))
  const temporalFragShader = createShader(gl, gl.FRAGMENT_SHADER, temporalFragmentShader)
  const temporalProgram = createProgram(gl, temporalVertexShader, temporalFragShader)
  const temporalUniforms = {
    position: gl.getAttribLocation(temporalProgram, 'position'),
    currentMask: gl.getUniformLocation(temporalProgram, 'u_currentMask'),
    previousMask: gl.getUniformLocation(temporalProgram, 'u_previousMask'),
    combineRatio: gl.getUniformLocation(temporalProgram, 'u_combineRatio'),
    invertMask: gl.getUniformLocation(temporalProgram, 'u_invertMask'),
  }

  const gaussianBlur = createBlurProgram(gl, blurFragmentShader)
  const boxBlur = createBlurProgram(gl, boxBlurFragmentShader)

  const bgTexture = initTexture(gl, 0)
  const frameTexture = initTexture(gl, 1)
  const vertexBuffer = createVertexBuffer(gl)

  let bgBlurTextureWidth = Math.max(1, Math.floor(fboWidth / DOWNSAMPLE_FACTOR))
  let bgBlurTextureHeight = Math.max(1, Math.floor(fboHeight / DOWNSAMPLE_FACTOR))
  const bgBlurTextures = [initTexture(gl, 3), initTexture(gl, 4)]
  const bgBlurFramebuffers = [
    createFramebuffer(gl, bgBlurTextures[0], bgBlurTextureWidth, bgBlurTextureHeight),
    createFramebuffer(gl, bgBlurTextures[1], bgBlurTextureWidth, bgBlurTextureHeight),
  ]
  const downSampler = createDownSampler(gl, bgBlurTextureWidth, bgBlurTextureHeight)

  const maskBlurTextures = [initTexture(gl, 6), initTexture(gl, 7)]
  const maskBlurFramebuffers = [
    createFramebuffer(gl, maskBlurTextures[0], fboWidth, fboHeight),
    createFramebuffer(gl, maskBlurTextures[1], fboWidth, fboHeight),
  ]

  const temporalTextures = [initTexture(gl, 8), initTexture(gl, 9)]
  const temporalFramebuffers = [
    createFramebuffer(gl, temporalTextures[0], fboWidth, fboHeight),
    createFramebuffer(gl, temporalTextures[1], fboWidth, fboHeight),
  ]
  let temporalReadIndex = 0
  let temporalWriteIndex = 1

  let backgroundImageDisabled = false
  let customBackgroundImage: ImageBitmap | ImageData | null = null
  let sourceBackgroundImage: ImageBitmap | null = null
  let hasMask = false

  gl.useProgram(compositeProgram)
  gl.uniform1i(compositeUniforms.disableBackground, 0)
  gl.uniform1i(compositeUniforms.background, 0)
  gl.uniform1i(compositeUniforms.frame, 1)
  gl.uniform1i(compositeUniforms.mask, 2)
  gl.uniform1f(compositeUniforms.edgeLow, tuning.edgeLow)
  gl.uniform1f(compositeUniforms.edgeHigh, tuning.edgeHigh)

  gl.useProgram(temporalProgram)
  gl.uniform1i(temporalUniforms.currentMask, 0)
  gl.uniform1i(temporalUniforms.previousMask, 1)
  gl.uniform1f(temporalUniforms.combineRatio, tuning.combineWithPreviousRatio)
  gl.uniform1i(temporalUniforms.invertMask, 0)

  function ensureTargetSize(width: number, height: number) {
    const targetWidth = Math.max(1, width)
    const targetHeight = Math.max(1, height)
    if (targetWidth === fboWidth && targetHeight === fboHeight) {
      return
    }
    fboWidth = targetWidth
    fboHeight = targetHeight
    bgBlurTextureWidth = Math.max(1, Math.floor(fboWidth / DOWNSAMPLE_FACTOR))
    bgBlurTextureHeight = Math.max(1, Math.floor(fboHeight / DOWNSAMPLE_FACTOR))
    gl.activeTexture(gl.TEXTURE0)
    for (const texture of [...bgBlurTextures, downSampler.texture]) {
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        bgBlurTextureWidth,
        bgBlurTextureHeight,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null,
      )
    }
    for (const texture of [...maskBlurTextures, ...temporalTextures]) {
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, fboWidth, fboHeight, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    }
    hasMask = false
    if (sourceBackgroundImage) {
      void setBackgroundImage(sourceBackgroundImage)
    }
  }

  function renderFrame(frame: VideoFrame) {
    if (frame.codedWidth === 0) {
      return
    }
    const width = frame.displayWidth
    const height = frame.displayHeight
    ensureTargetSize(width, height)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, frameTexture)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, frame)

    let backgroundTexture = bgTexture
    if (blurRadius) {
      const downSampledFrameTexture = applyDownsampling(
        gl,
        frameTexture,
        downSampler,
        vertexBuffer,
        bgBlurTextureWidth,
        bgBlurTextureHeight,
      )
      backgroundTexture = applyBlur(
        gl,
        downSampledFrameTexture,
        bgBlurTextureWidth,
        bgBlurTextureHeight,
        blurRadius,
        gaussianBlur,
        vertexBuffer,
        bgBlurFramebuffers,
        bgBlurTextures,
      )
    }

    gl.viewport(0, 0, width, height)
    gl.clearColor(1, 1, 1, 1)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.useProgram(compositeProgram)
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer)
    gl.vertexAttribPointer(compositeUniforms.position, 2, gl.FLOAT, false, 0, 0)
    gl.enableVertexAttribArray(compositeUniforms.position)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, backgroundTexture)
    gl.uniform1i(compositeUniforms.background, 0)
    gl.uniform1i(
      compositeUniforms.disableBackground,
      backgroundImageDisabled || !hasMask ? 1 : 0,
    )
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, frameTexture)
    gl.uniform1i(compositeUniforms.frame, 1)
    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, maskBlurTextures[1])
    gl.uniform1i(compositeUniforms.mask, 2)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
  }

  function updateMask(maskTexture: WebGLTexture) {
    gl.useProgram(temporalProgram)
    gl.bindFramebuffer(gl.FRAMEBUFFER, temporalFramebuffers[temporalWriteIndex])
    gl.viewport(0, 0, fboWidth, fboHeight)
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer)
    gl.vertexAttribPointer(temporalUniforms.position, 2, gl.FLOAT, false, 0, 0)
    gl.enableVertexAttribArray(temporalUniforms.position)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, maskTexture)
    gl.uniform1i(temporalUniforms.currentMask, 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, temporalTextures[temporalReadIndex])
    gl.uniform1i(temporalUniforms.previousMask, 1)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)

    applyBlur(
      gl,
      temporalTextures[temporalWriteIndex],
      fboWidth,
      fboHeight,
      tuning.maskBlurRadius,
      boxBlur,
      vertexBuffer,
      maskBlurFramebuffers,
      maskBlurTextures,
    )

    temporalReadIndex = temporalWriteIndex
    temporalWriteIndex = 1 - temporalWriteIndex
    hasMask = true
  }

  async function setBackgroundImage(image: ImageBitmap | null) {
    sourceBackgroundImage = image
    if (customBackgroundImage instanceof ImageBitmap) {
      customBackgroundImage.close()
    }
    customBackgroundImage = null
    if (!image) {
      return
    }
    let resizedImage: ImageBitmap | null = null
    try {
      resizedImage = await resizeImageToCover(image, fboWidth, fboHeight)
    } catch (_error) {
      void _error
    }
    if (sourceBackgroundImage !== image) {
      resizedImage?.close()
      return
    }
    if (customBackgroundImage instanceof ImageBitmap) {
      customBackgroundImage.close()
    }
    customBackgroundImage = resizedImage ?? new ImageData(2, 2)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, bgTexture)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, customBackgroundImage)
  }

  function setBlurRadius(radius: number | null) {
    blurRadius = radius ? Math.max(1, Math.floor(radius / DOWNSAMPLE_FACTOR)) : null
    if (blurRadius) {
      void setBackgroundImage(null)
    }
  }

  function setBackgroundDisabled(disabled: boolean) {
    backgroundImageDisabled = disabled
  }

  function setMaskInvert(invert: boolean) {
    gl.useProgram(temporalProgram)
    gl.uniform1i(temporalUniforms.invertMask, invert ? 1 : 0)
  }

  function cleanup() {
    gl.deleteProgram(compositeProgram)
    gl.deleteProgram(temporalProgram)
    gl.deleteProgram(gaussianBlur.program)
    gl.deleteProgram(boxBlur.program)
    gl.deleteProgram(downSampler.program)
    gl.deleteTexture(bgTexture)
    gl.deleteTexture(frameTexture)
    gl.deleteTexture(downSampler.texture)
    gl.deleteFramebuffer(downSampler.framebuffer)
    for (const texture of [...bgBlurTextures, ...maskBlurTextures, ...temporalTextures]) {
      gl.deleteTexture(texture)
    }
    for (const framebuffer of [
      ...bgBlurFramebuffers,
      ...maskBlurFramebuffers,
      ...temporalFramebuffers,
    ]) {
      gl.deleteFramebuffer(framebuffer)
    }
    gl.deleteBuffer(vertexBuffer)
    if (customBackgroundImage instanceof ImageBitmap) {
      customBackgroundImage.close()
    }
    customBackgroundImage = null
    sourceBackgroundImage = null
  }

  return {
    renderFrame,
    updateMask,
    setBackgroundImage,
    setBlurRadius,
    setBackgroundDisabled,
    setMaskInvert,
    cleanup,
  }
}
